import * as React from "react";
import { Link } from "@talentpair/routing";
import { SvgIconProps } from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { Waypoint } from "react-waypoint";
import { FlatButton } from "@talentpair/quantic";
import { queenstownLink } from "@talentpair/env";
import { PaginationT } from "@talentpair/types/misc";
import { PublicJobT } from "@talentpair/types/public";
import { displayMultipleLocations } from "kyoto/utils/entities/location";
import { pluralize, truncateTextByWordCount } from "kyoto/utils/string";
import { displayCapJobRemoteOption } from "kyoto/utils/entities/misc";
import JobHeader from "../../components/JobHeader";
import EmptyResults from "./EmptyResults";
import SignupCtaResult from "./SignupCtaResult";
import { OrgBoardDetailT } from "../../types";

const InfoChip =
  (Icon: React.ComponentType<SvgIconProps>) =>
  ({ children }: { children: React.ReactNode }) =>
    (
      <div className="flex items-center">
        <Icon classes={{ root: "grey-700" }} style={{ width: 18, height: 18 }} />
        <span className="grey-700 h6 pl1">{children}</span>
      </div>
    );

const LocationChip = InfoChip(LocationOnIcon);

interface ResultItemProps {
  job: PublicJobT;
  jobLink: (job: PublicJobT) => string;
  org: OrgBoardDetailT;
  slug: string;
}

export const ResultItem = ({ job, jobLink, org, slug }: ResultItemProps): React.ReactElement => {
  const pathname = jobLink(job);
  const { role, organization, locations, remote } = job;
  const locationText = displayMultipleLocations(locations);
  return (
    <div className="card-outline mb3">
      <div className="p2 grey-border-bottom">
        {organization ? (
          <JobHeader
            job={job}
            pathname={pathname}
            isLink
            orgLink={
              slug === "tp" || org.is_vc
                ? queenstownLink.orgDetail(organization.id)
                : org.job_count > 10
                ? queenstownLink.orgAboutUs()
                : null
            }
          />
        ) : (
          <span className="h2 semibold">
            {role ? (
              <Link
                className="black hover-underline"
                to={{
                  pathname,
                  state: { backLink: `${location.pathname}${location.search}` },
                }}
              >
                {role.name}
              </Link>
            ) : (
              "Unknown Role"
            )}
          </span>
        )}
      </div>
      <div className="p3">
        <span>
          {locationText || remote ? (
            <LocationChip>
              {locationText.toUpperCase()}
              {locationText && remote ? " • " : null}
              {remote ? (
                <span className="black bold">{displayCapJobRemoteOption(job.remote)}</span>
              ) : null}
            </LocationChip>
          ) : null}
        </span>
        <div className="mt3 wrap-all">{truncateTextByWordCount(job.summary)}</div>
      </div>
      <div className="p2 grey-border-top">
        <Link
          to={{
            pathname,
            state: { backLink: `${location.pathname}${location.search}` },
          }}
        >
          <FlatButton color="primary">Learn More and Apply</FlatButton>
        </Link>
      </div>
    </div>
  );
};

interface ResultsListPropsT {
  results: PublicJobT[];
  pagination: PaginationT;
  fetchResults: (url: string) => Promise<void>;
  jobLink: (job: PublicJobT) => string;
  showOrganizations?: boolean;
  org: OrgBoardDetailT;
  slug: string;
}

export const ResultsList = ({
  results,
  pagination,
  fetchResults,
  jobLink,
  org,
  slug,
  showOrganizations,
}: ResultsListPropsT): React.ReactElement => (
  <>
    <div className="h2 bold p3">{`${pagination.count} ${pluralize("Job", pagination.count)}`}</div>
    {results.length ? (
      results.map((job) => (
        <ResultItem key={job.id} job={job} jobLink={jobLink} org={org} slug={slug} />
      ))
    ) : (
      <EmptyResults checkOrganizations={showOrganizations} />
    )}
    {results.length && !pagination.next ? <SignupCtaResult /> : null}
    {pagination.next ? (
      <Waypoint
        key={pagination.next}
        bottomOffset="-50%"
        onEnter={() => pagination.next && fetchResults(pagination.next)}
      />
    ) : null}
  </>
);
